import { nameFields } from "../utils/individualName";
import { intakePlan, sameIntakeValue, confirmedKycFields, cvSourceLabels, prepareIntakeFiles, intakeRequest, categoryLabels } from '../utils/clientDocumentIntake';

// Both drop zones must use the same extraction contract and document values.
export async function extractKycDocuments(files, form, signal, onProgress) {
  const prepared = await prepareIntakeFiles(files);
  const result = await intakeRequest('extract', { files: prepared, context: {}, scope: 'kyc' }, signal, onProgress);
  return {
    documents: result.documents.filter(d => d.status === 'extracted' && categoryLabels[d.category])
      .map(d => ({ ...d, file: prepared.find(f => f.id === d.id) })),
    unidentified: result.documents.some(d => d.status === 'not_identified' || d.category === 'not_identified'),
    failed: result.documents.some(d => !['extracted', 'duplicate', 'not_identified'].includes(d.status)),
  };
}

export function intakeDate(value) {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
}

export function kycIntakeContext(form) {
  const c = form.customer || {};
  return { ...nameFields(c), clientName: c.name || '', clientType: c.type || '', address: c.address || '', city: c.city || '', country: c.country || '',
    contactEmail: c.email || '', contactPhone: c.phone || '',
    ...(c.type === 'entity' ? { incorporationDate: intakeDate(c.dobOrIncorporationDate), incorporationCountry: c.nationalityOrIncorporationCountry || '' }
      : { dateOfBirth: intakeDate(c.dobOrIncorporationDate), nationality: c.nationalityOrIncorporationCountry || '' }) };
}

export function kycIntakeRows(documents, form, separateNames = false) {
  // Business activity is compared locally; it is not an accepted API context field.
  const context = { ...kycIntakeContext(form), businessActivity: form.customer?.businessActivity || '' };
  const plan = intakePlan(documents, context, [], { reviewExisting: true, separateNames });
  const rows = [...plan.review];
  Object.entries(plan.autoFill).forEach(([field,value]) => rows.push({field,currentValue:context[field] || '',options:[{value,documentId:plan.autoSources[field][0]}],issues:[]}));
  Object.entries(plan.autoKyc).forEach(([field,item]) => rows.push({field,currentValue:context[field] || '',options:[item],issues:[]}));
  const cv = [...documents].reverse().find(d => d.category === 'cv');
  const activity = cv?.extraction?.cvProfile?.businessActivity;
  if (activity && !sameIntakeValue(activity, context.businessActivity)) rows.push({field:'businessActivity',currentValue:context.businessActivity,options:[{value:activity,documentId:cv.id}],issues:[]});
  const wealth = form.sourceOfFundsWealth?.sourceOfWealth || [];
  if (!form.intakeSubjectOnly && context.clientType !== 'entity') (cv?.extraction?.cvProfile?.suggestions || []).forEach(s => {
    if (cvSourceLabels[s.category] && !wealth.includes(s.category)) rows.push({field:`wealth:${s.category}`,currentValue:'',options:[{value:s.category,documentId:cv.id}],issues:[], explanation:s.explanation});
  });
  return rows;
}

export function applyKycIntake(form, values) {
  const customer = { ...form.customer };
  const keys = {givenNames:'givenNames',familyName:'familyName',clientName:'name',clientType:'type',address:'address',city:'city',country:'country',contactPhone:'phone',contactEmail:'email',businessActivity:'businessActivity'};
  Object.entries(keys).forEach(([from,to]) => { if (values[from] !== undefined) customer[to] = values[from]; });
  Object.assign(customer, confirmedKycFields(values, customer.type));
  const wealth = Object.keys(values).filter(k => k.startsWith('wealth:')).map(k=>k.slice(7)).filter(k=>cvSourceLabels[k]);
  return {...form,customer,sourceOfFundsWealth:{...form.sourceOfFundsWealth,sourceOfWealth:[...new Set([...(form.sourceOfFundsWealth?.sourceOfWealth || []),...wealth])]}};
}
